<template>
  <ContentWrap
    title="APP配置"
    message="C 端「启动页 → 引导页 → 登录页 → 首页」文案，保存在 bosscircle_config.APP_COPY，App 下次启动生效"
  >
    <template #header>
      <el-button :loading="loading" size="small" @click="load">
        <Icon icon="ep:refresh" class="mr-4px" />刷新
      </el-button>
      <el-button :loading="saving" size="small" type="primary" @click="save">
        <Icon icon="ep:check" class="mr-4px" />保存
      </el-button>
      <el-button :loading="resetting" size="small" @click="reset">
        <Icon icon="ep:refresh-left" class="mr-4px" />恢复默认
      </el-button>
    </template>

    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      show-icon
      title="保存即写入数据库，C 端下次启动 App 时拉取生效（无热更新）。留空的字段会由 App 内置默认文案兜底，不会白屏。"
    />

    <el-tabs v-model="tab" @tab-change="onTabChange">
      <!-- ───────────── 启动页 ───────────── -->
      <el-tab-pane label="启动页" name="startup">
        <el-form v-loading="loading" :model="form.startup" label-width="160px" class="max-w-900px">
          <el-divider content-position="left">开屏画面</el-divider>
          <el-form-item label="主标题">
            <el-input v-model="form.startup.title" placeholder="boss圈儿" clearable />
            <span class="config-tip">开屏大字，如「boss圈儿」</span>
          </el-form-item>
          <el-form-item label="副标题">
            <el-input v-model="form.startup.subtitle" placeholder="遇 见 贵 人" clearable />
            <span class="config-tip">开屏第二行，字间距由 App 控制，这里只填文字</span>
          </el-form-item>
          <el-form-item label="Logo 图片地址">
            <el-input v-model="form.startup.logoUrl" placeholder="https://... 留空则用文字标题" clearable />
            <el-image
              v-if="form.startup.logoUrl"
              :src="form.startup.logoUrl"
              fit="contain"
              class="config-preview-img"
              :preview-src-list="[form.startup.logoUrl]"
              preview-teleported
            />
          </el-form-item>
          <el-form-item label="背景图地址">
            <el-input v-model="form.startup.backgroundUrl" placeholder="https://... 留空则用内置星空背景" clearable />
            <el-image
              v-if="form.startup.backgroundUrl"
              :src="form.startup.backgroundUrl"
              fit="cover"
              class="config-preview-img"
              :preview-src-list="[form.startup.backgroundUrl]"
              preview-teleported
            />
          </el-form-item>
          <el-form-item label="停留时长（毫秒）">
            <el-input-number
              v-model="form.startup.durationMs"
              :min="500"
              :max="10000"
              :step="100"
              controls-position="right"
              class="!w-200px"
            />
            <span class="config-tip">默认 3300；太短会闪一下，建议 ≥ 2000</span>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- ───────────── 引导页 ───────────── -->
      <el-tab-pane label="引导页" name="onboarding">
        <el-row :gutter="20">
          <el-col :xs="24" :lg="15">
            <el-form
              v-loading="loading"
              :model="form.onboarding"
              label-width="150px"
              class="max-w-900px"
            >
              <el-divider content-position="left">欢迎页（第一屏）</el-divider>
              <el-form-item label="顶部小标签">
                <el-input v-model="form.onboarding.welcomeLabel" placeholder="WELCOME" clearable />
                <span class="config-tip">标题上方的英文小字，默认 WELCOME</span>
              </el-form-item>
              <el-form-item label="主标题">
                <el-input
                  v-model="form.onboarding.welcomeTitle"
                  type="textarea"
                  :rows="2"
                  placeholder="上boss圈儿，&#10;遇见贵人"
                />
                <span class="config-tip">支持换行，直接回车即可</span>
              </el-form-item>
              <el-form-item label="说明文案">
                <el-input
                  v-model="form.onboarding.welcomeDesc"
                  type="textarea"
                  :rows="3"
                  placeholder="这里不是又一个社交软件……"
                />
              </el-form-item>
              <el-form-item label="卡片角标">
                <el-input v-model="form.onboarding.welcomeBadge" placeholder="✦ 每个人都是一颗星" clearable />
                <span class="config-tip">星海卡片左下角的小标签</span>
              </el-form-item>

              <el-divider content-position="left">按钮文案</el-divider>
              <el-form-item label="主按钮（四步中）">
                <el-input v-model="form.onboarding.primaryAction" placeholder="下一步" clearable />
              </el-form-item>
              <el-form-item label="主按钮（最后一步）">
                <el-input v-model="form.onboarding.finalAction" placeholder="开启我的星海" clearable />
              </el-form-item>
              <el-form-item label="跳过按钮">
                <el-input v-model="form.onboarding.skipAction" placeholder="跳过，先逛逛" clearable />
              </el-form-item>

              <el-divider content-position="left">探索者 / 手机号（当前版本未展示）</el-divider>
              <el-form-item label="探索者序号">
                <el-input
                  v-model="form.onboarding.explorerLabel"
                  placeholder="天枢 · 第 {index} 位探索者"
                  clearable
                />
                <span class="config-tip">`{index}` 会替换成真实注册序号；STEP04 汇总屏已删除，暂无展示位</span>
              </el-form-item>
              <el-form-item label="手机号核验文案">
                <el-input
                  v-model="form.onboarding.phoneVerifiedLabel"
                  placeholder="本机号码 · 已通过运营商核验"
                  clearable
                />
              </el-form-item>
            </el-form>

            <el-divider content-position="left">
              三步引导标题（顺序固定：资料 → 标签 → 方向；方向之后直接进登录页）
            </el-divider>
            <el-form
              v-for="(step, index) in form.onboarding.steps"
              :key="step.code"
              label-width="120px"
              class="step-form"
            >
              <div class="step-title">
                {{ index + 1 }}. {{ STEP_NAMES[step.code] || step.code }}
                <el-tag size="small" type="info" effect="plain">{{ step.code }}</el-tag>
              </div>
              <el-form-item label="步骤标签">
                <el-input v-model="step.label" :placeholder="`STEP 0${index + 1} / 04`" clearable />
              </el-form-item>
              <el-form-item label="标题">
                <el-input v-model="step.title" type="textarea" :rows="2" />
              </el-form-item>
              <el-form-item label="说明">
                <el-input v-model="step.desc" type="textarea" :rows="2" placeholder="可留空" />
              </el-form-item>
            </el-form>
          </el-col>

          <el-col :xs="24" :lg="9">
            <div class="preview-label">效果预览</div>
            <div class="phone-preview">
              <div class="preview-welcome">{{ form.onboarding.welcomeLabel || 'WELCOME' }}</div>
              <div class="preview-title">{{ form.onboarding.welcomeTitle || '上boss圈儿，遇见贵人' }}</div>
              <div class="preview-desc">{{ form.onboarding.welcomeDesc }}</div>
              <div class="preview-card">
                <span class="preview-badge">{{ form.onboarding.welcomeBadge }}</span>
              </div>
              <div class="preview-primary">{{ form.onboarding.primaryAction || '下一步' }}</div>
              <div class="preview-skip">{{ form.onboarding.skipAction || '跳过，先逛逛' }}</div>
            </div>
            <div class="preview-tip">预览只示意文字，实际排版以 App 为准</div>
          </el-col>
        </el-row>
      </el-tab-pane>

      <!-- ───────────── 标签 / 方向（STEP02 / STEP03） ───────────── -->
      <el-tab-pane label="标签 / 方向" name="choices">
        <el-alert
          class="mb-16px"
          type="info"
          :closable="false"
          show-icon
          title="第二步「标签」与第三步「关注方向」的候选项和默认选中项。标签至少保留 3 个、最多 8 个可选；清空候选会自动回落默认列表。"
        />
        <el-row :gutter="24">
          <el-col :xs="24" :lg="12">
            <el-form label-width="120px">
              <el-divider content-position="left">第二步 · 标签候选</el-divider>
              <el-form-item label="候选项">
                <el-select
                  v-model="form.onboarding.tags"
                  multiple
                  filterable
                  allow-create
                  default-first-option
                  placeholder="输入后回车新增"
                  class="!w-100%"
                >
                  <el-option
                    v-for="item in form.onboarding.tags"
                    :key="item"
                    :label="item"
                    :value="item"
                  />
                </el-select>
                <span class="config-tip">顺序即 C 端展示顺序</span>
              </el-form-item>
              <el-form-item label="默认选中">
                <el-select
                  v-model="form.onboarding.tagsSelected"
                  multiple
                  filterable
                  placeholder="留空则取候选前 5 个"
                  class="!w-100%"
                >
                  <el-option
                    v-for="item in form.onboarding.tags"
                    :key="item"
                    :label="item"
                    :value="item"
                  />
                </el-select>
                <span class="config-tip">预选超过 8 个会被 C 端拦下</span>
              </el-form-item>
              <el-form-item label="提示文案">
                <el-input v-model="form.fields.tagHint" placeholder="建议 3—8 个 · 点击可增减" clearable />
              </el-form-item>
            </el-form>
          </el-col>
          <el-col :xs="24" :lg="12">
            <el-form label-width="120px">
              <el-divider content-position="left">第三步 · 方向候选</el-divider>
              <el-form-item label="候选项">
                <el-select
                  v-model="form.onboarding.directions"
                  multiple
                  filterable
                  allow-create
                  default-first-option
                  placeholder="输入后回车新增"
                  class="!w-100%"
                >
                  <el-option
                    v-for="item in form.onboarding.directions"
                    :key="item"
                    :label="item"
                    :value="item"
                  />
                </el-select>
                <span class="config-tip">顺序即 C 端展示顺序</span>
              </el-form-item>
              <el-form-item label="默认选中">
                <el-select
                  v-model="form.onboarding.directionsSelected"
                  multiple
                  filterable
                  placeholder="留空则取候选前 4 个"
                  class="!w-100%"
                >
                  <el-option
                    v-for="item in form.onboarding.directions"
                    :key="item"
                    :label="item"
                    :value="item"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="提示文案">
                <el-input
                  v-model="form.fields.directionHint"
                  placeholder="随时可在设置中调整"
                  clearable
                />
              </el-form-item>
            </el-form>
          </el-col>
        </el-row>
      </el-tab-pane>

      <!-- ───────────── 资料表单（引导 STEP01） ───────────── -->
      <el-tab-pane label="资料表单" name="form">
        <el-alert
          class="mb-16px"
          type="info"
          :closable="false"
          show-icon
          title="引导页第一步「先认识您」的字段名与输入提示。只影响显示文字，不影响提交的字段。"
        />
        <el-form v-loading="loading" :model="form.fields" label-width="180px" class="max-w-900px">
          <el-divider content-position="left">昵称</el-divider>
          <el-form-item label="字段名">
            <el-input v-model="form.fields.nameLabel" placeholder="昵称" clearable />
          </el-form-item>
          <el-form-item label="输入提示">
            <el-input v-model="form.fields.nameHint" placeholder="请输入昵称" clearable />
          </el-form-item>

          <el-divider content-position="left">性别</el-divider>
          <el-form-item label="字段名">
            <el-input v-model="form.fields.genderLabel" placeholder="性别" clearable />
          </el-form-item>
          <el-form-item label="可选项">
            <el-select
              v-model="form.fields.genderOptions"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="输入后回车新增，至少保留 1 项"
              class="!w-400px"
            >
              <el-option v-for="item in form.fields.genderOptions" :key="item" :label="item" :value="item" />
            </el-select>
            <span class="config-tip">顺序即 C 端展示顺序</span>
          </el-form-item>

          <el-divider content-position="left">出生年月</el-divider>
          <el-form-item label="字段名">
            <el-input
              v-model="form.fields.birthLabel"
              placeholder="出生年月（用于判定生肖与星座）"
              clearable
            />
          </el-form-item>
          <el-form-item label="未选择时占位">
            <el-input v-model="form.fields.birthEmpty" placeholder="未填写" clearable />
          </el-form-item>

          <el-divider content-position="left">城市</el-divider>
          <el-form-item label="字段名">
            <el-input v-model="form.fields.cityLabel" placeholder="所在城市" clearable />
          </el-form-item>
          <el-form-item label="输入提示">
            <el-input v-model="form.fields.cityHint" placeholder="请选择所在城市" clearable />
          </el-form-item>

          <el-divider content-position="left">行业 / 职位</el-divider>
          <el-form-item label="字段名">
            <el-input v-model="form.fields.industryLabel" placeholder="行业 / 职位" clearable />
          </el-form-item>
          <el-form-item label="输入提示">
            <el-input
              v-model="form.fields.industryHint"
              placeholder="如：新材料 · 产品负责人"
              clearable
            />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- ───────────── 行业 / 职位（STEP01 弹窗） ───────────── -->
      <el-tab-pane label="行业 / 职位" name="industry">
        <el-alert
          class="mb-16px"
          type="info"
          :closable="false"
          show-icon
          title="引导页「行业 / 职位」弹窗的候选项。C 端是两段选择：先选行业、再选职位，最后拼成「行业 · 职位」。留空则用 App 内置的 24 个常用行业 / 23 个常用职位。"
        />
        <el-form v-loading="loading" :model="form.fields" label-width="140px" class="max-w-900px">
          <el-form-item label="行业候选">
            <el-select
              v-model="form.fields.industries"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="输入后回车新增"
              class="!w-100%"
            >
              <el-option
                v-for="item in form.fields.industries"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
            <span class="config-tip">顺序即 C 端展示顺序，点标签右侧 × 可删除</span>
          </el-form-item>
          <el-form-item label="职位候选">
            <el-select
              v-model="form.fields.positions"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="输入后回车新增"
              class="!w-100%"
            >
              <el-option
                v-for="item in form.fields.positions"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
            <span class="config-tip">顺序即 C 端展示顺序</span>
          </el-form-item>
          <el-form-item label="当前数量">
            <el-tag type="info" effect="plain">
              行业 {{ form.fields.industries.length }} 个 · 职位
              {{ form.fields.positions.length }} 个
            </el-tag>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- ───────────── 登录页 ───────────── -->
      <el-tab-pane label="登录页" name="login">
        <el-form v-loading="loading" :model="form.login" label-width="160px" class="max-w-900px">
          <el-divider content-position="left">品牌区</el-divider>
          <el-form-item label="主标题">
            <el-input v-model="form.login.title" placeholder="boss圈儿" clearable />
          </el-form-item>
          <el-form-item label="副标题">
            <el-input v-model="form.login.subtitle" placeholder="遇 见 贵 人" clearable />
          </el-form-item>
          <el-form-item label="标语">
            <el-input v-model="form.login.tagline" placeholder="boss圈儿，遇见贵人，成为贵人" clearable />
          </el-form-item>

          <el-divider content-position="left">城市定位</el-divider>
          <el-form-item label="默认城市">
            <el-input v-model="form.login.defaultCity" placeholder="昆明" clearable />
            <span class="config-tip">拿不到定位时的兜底城市</span>
          </el-form-item>
          <el-form-item label="定位文案">
            <el-input v-model="form.login.cityLabel" placeholder="{city}市 · 一键定位" clearable />
            <span class="config-tip">`{city}` 会替换成当前城市</span>
          </el-form-item>

          <el-divider content-position="left">登录方式</el-divider>
          <el-form-item label="微信登录按钮">
            <el-input v-model="form.login.wechatAction" placeholder="◉ 微信一键登录" clearable />
          </el-form-item>
          <el-form-item label="手机号登录按钮">
            <el-input v-model="form.login.mobileAction" placeholder="◍ 手机号登录" clearable />
          </el-form-item>
          <el-form-item label="安全提示">
            <el-input v-model="form.login.secureTip" placeholder="🔒 商务级加密 · 登录后核验身份" clearable />
          </el-form-item>

          <el-divider content-position="left">协议文案</el-divider>
          <el-form-item label="协议引导语">
            <el-input v-model="form.login.agreementPrefix" placeholder="我已阅读并同意" clearable />
          </el-form-item>
          <el-form-item label="用户协议">
            <el-input v-model="form.login.agreementUser" placeholder="《用户协议》" clearable />
          </el-form-item>
          <el-form-item label="连接词">
            <el-input v-model="form.login.agreementAnd" placeholder="和" clearable />
          </el-form-item>
          <el-form-item label="隐私政策">
            <el-input v-model="form.login.agreementPrivacy" placeholder="《隐私政策》" clearable />
          </el-form-item>
          <el-form-item label="未勾选提示">
            <el-input
              v-model="form.login.agreementHint"
              placeholder="◉ 请先勾选协议，否则无法登录"
              clearable
            />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- ───────────── 首页 ───────────── -->
      <el-tab-pane label="首页" name="home">
        <el-form v-loading="loading" :model="form.home" label-width="160px" class="max-w-900px">
          <el-form-item label="问候语兜底">
            <el-input v-model="form.home.greetingFallback" placeholder="今日为您" clearable />
            <span class="config-tip">问候接口失败时显示</span>
          </el-form-item>
          <el-form-item label="主标题">
            <el-input v-model="form.home.heroTitle" placeholder="浩瀚宇宙，{name}" clearable />
            <span class="config-tip">`{name}` 会替换成用户昵称</span>
          </el-form-item>
          <el-form-item label="副标题">
            <el-input v-model="form.home.heroSubtitle" placeholder="您是最闪亮的星" clearable />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- ───────────── 高级 JSON ───────────── -->
      <el-tab-pane label="高级 JSON" name="json">
        <el-alert
          class="mb-12px"
          type="warning"
          :closable="false"
          show-icon
          title="直接编辑整段 JSON，保存前会校验格式。字段名必须与 App 约定一致（startup / onboarding / login / home），写错字段会被 App 忽略。"
        />
        <el-input v-model="jsonText" type="textarea" :rows="24" class="json-box" spellcheck="false" />
        <div class="mt-8px">
          <el-button size="small" @click="formatJson">格式化</el-button>
          <el-button size="small" @click="applyJson">应用到表单</el-button>
        </div>
      </el-tab-pane>
    </el-tabs>
  </ContentWrap>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/appCopy'

defineOptions({ name: 'BossCircleAppCopy' })

const message = useMessage()

const STEP_NAMES: Record<string, string> = {
  profile: '先认识您（资料）',
  positioning: '您希望别人怎么认识您（标签）',
  direction: '您想关注什么方向（方向）'
}

/** 与 App 内置默认值保持一致：后台没配时页面也能正常显示 */
const DEFAULTS = {
  startup: {
    title: 'boss圈儿',
    subtitle: '遇 见 贵 人',
    logoUrl: '',
    backgroundUrl: '',
    durationMs: 3300
  },
  onboarding: {
    welcomeLabel: 'WELCOME',
    welcomeTitle: '上boss圈儿，\n遇见贵人',
    welcomeDesc:
      '这里不是又一个社交软件。我们把与您同频的人，放在一片星海里。点亮一颗星，就是打开一次可能的合作。',
    welcomeBadge: '✦ 每个人都是一颗星',
    primaryAction: '下一步',
    finalAction: '开启我的星海',
    skipAction: '跳过，先逛逛',
    explorerLabel: '天枢 · 第 {index} 位探索者',
    phoneVerifiedLabel: '本机号码 · 已通过运营商核验',
    tags: [
      '新材料',
      '找投资',
      '找供应链',
      'AI 创业',
      '品牌营销',
      '找项目',
      '找渠道',
      '找合伙人',
      '跨境出海',
      '智能制造',
      '法律合规',
      '财税服务',
      '人才招聘',
      '企业服务',
      '政府资源',
      '市场拓展'
    ],
    tagsSelected: ['新材料', '找投资', '找供应链', 'AI 创业', '品牌营销'],
    directions: [
      '商机对接',
      '融资路演',
      '行业活动',
      '供应链',
      '政策补贴',
      '技术交流',
      '展会资讯',
      '私董会',
      '行业报告',
      '跨境贸易',
      '园区招商',
      '品牌推广'
    ],
    directionsSelected: ['商机对接', '融资路演', '行业活动', '供应链'],
    steps: [] as { code: string; label: string; title: string; desc: string }[]
  },
  form: {
    nameLabel: '昵称',
    nameHint: '请输入昵称',
    genderLabel: '性别',
    genderOptions: ['男', '女', '不公开'],
    birthLabel: '出生年月（用于判定生肖与星座）',
    birthEmpty: '未填写',
    cityLabel: '所在城市',
    cityHint: '请选择所在城市',
    industryLabel: '行业 / 职位',
    industryHint: '如：新材料 · 产品负责人',
    industries: [
      '互联网 / 人工智能',
      '新能源',
      '新材料',
      '先进制造',
      '生物医药',
      '医疗器械',
      '金融服务',
      '投资机构',
      '房地产 / 建筑',
      '教育培训',
      '法律合规',
      '财税服务',
      '人力资源',
      '市场营销',
      '文化传媒',
      '跨境电商',
      '国际贸易',
      '物流供应链',
      '农业食品',
      '文旅酒店',
      '汽车产业',
      '能源电力',
      '环保工程',
      '政府 / 公共事业'
    ],
    positions: [
      '创始人 / CEO',
      '联合创始人',
      '总经理',
      '副总裁',
      '合伙人',
      'CTO',
      'COO',
      'CFO',
      'CMO',
      '技术总监',
      '产品总监',
      '运营总监',
      '市场总监',
      '销售总监',
      '供应链总监',
      '人力资源总监',
      '法务总监',
      '投资经理',
      '项目经理',
      '架构师',
      '高级工程师',
      '设计师',
      '咨询顾问'
    ],
    tagHint: '建议 3—8 个 · 点击可增减',
    directionHint: '随时可在设置中调整'
  },
  login: {
    title: 'boss圈儿',
    subtitle: '遇 见 贵 人',
    tagline: 'boss圈儿，遇见贵人，成为贵人',
    defaultCity: '昆明',
    cityLabel: '{city}市 · 一键定位',
    wechatAction: '◉ 微信一键登录',
    mobileAction: '◍ 手机号登录',
    agreementPrefix: '我已阅读并同意',
    agreementUser: '《用户协议》',
    agreementAnd: '和',
    agreementPrivacy: '《隐私政策》',
    agreementHint: '◉ 请先勾选协议，否则无法登录',
    secureTip: '🔒 商务级加密 · 登录后核验身份'
  },
  home: {
    greetingFallback: '今日为您',
    heroTitle: '浩瀚宇宙，{name}',
    heroSubtitle: '您是最闪亮的星'
  }
}

const DEFAULT_STEPS = [
  {
    code: 'profile',
    label: 'STEP 01 / 03',
    title: '先认识您',
    desc: '填写基础信息，我们会为您匹配同属、合拍的人脉。'
  },
  { code: 'positioning', label: 'STEP 02 / 03', title: '您希望别人\n怎么认识您', desc: '' },
  { code: 'direction', label: 'STEP 03 / 03', title: '您想关注\n什么方向', desc: '' }
]

const loading = ref(false)
const saving = ref(false)
const resetting = ref(false)
const tab = ref('startup')
const jsonText = ref('')

const form = reactive({
  startup: { ...DEFAULTS.startup },
  onboarding: {
    ...DEFAULTS.onboarding,
    tags: [...DEFAULTS.onboarding.tags],
    tagsSelected: [...DEFAULTS.onboarding.tagsSelected],
    directions: [...DEFAULTS.onboarding.directions],
    directionsSelected: [...DEFAULTS.onboarding.directionsSelected],
    steps: DEFAULT_STEPS.map((item) => ({ ...item }))
  },
  fields: { ...DEFAULTS.form, genderOptions: [...DEFAULTS.form.genderOptions] },
  login: { ...DEFAULTS.login },
  home: { ...DEFAULTS.home }
})

/** 四步顺序固定，缺的按 code 补默认，多的丢掉——App 端只认这 4 个 code */
const normalizeSteps = (raw: unknown) => {
  const list = Array.isArray(raw) ? raw : []
  return DEFAULT_STEPS.map((fallback) => {
    const hit = list.find((item: any) => item?.code === fallback.code) as any
    return {
      code: fallback.code,
      label: String(hit?.label ?? fallback.label ?? ''),
      title: String(hit?.title ?? fallback.title ?? ''),
      desc: String(hit?.desc ?? fallback.desc ?? '')
    }
  })
}

/** 字符串数组：配了就用配的，没配/配空回落默认 */
const normalizeList = (raw: unknown, fallback: string[]): string[] => {
  if (!Array.isArray(raw)) return [...fallback]
  const list = raw.map((item) => String(item ?? '').trim()).filter((text) => text.length > 0)
  return list.length ? list : [...fallback]
}

/** 默认选中项：只保留候选里存在的；一个都不剩就取候选前 N 个（与 App 端一致） */
const normalizeSelected = (
  raw: unknown,
  rawOptions: unknown,
  fallbackOptions: string[],
  fallbackCount: number
): string[] => {
  const options = normalizeList(rawOptions, fallbackOptions)
  const picked = Array.isArray(raw)
    ? raw.map((item) => String(item ?? '').trim()).filter((text) => options.includes(text))
    : []
  return picked.length ? picked : options.slice(0, fallbackCount)
}

const applyJsonToForm = (parsed: any) => {
  const ob = parsed?.onboarding || {}
  form.startup = { ...DEFAULTS.startup, ...(parsed?.startup || {}) }
  form.onboarding = {
    ...DEFAULTS.onboarding,
    ...ob,
    // 选项列表配空会让 C 端没得选，这里回落默认
    tags: normalizeList(ob.tags, DEFAULTS.onboarding.tags),
    directions: normalizeList(ob.directions, DEFAULTS.onboarding.directions),
    // 默认选中项只保留候选里存在的，避免后台删了选项却还预选着
    tagsSelected: normalizeSelected(ob.tagsSelected, ob.tags, DEFAULTS.onboarding.tags, 5),
    directionsSelected: normalizeSelected(
      ob.directionsSelected,
      ob.directions,
      DEFAULTS.onboarding.directions,
      4
    ),
    steps: normalizeSteps(ob.steps)
  }
  const rawGender = parsed?.form?.genderOptions
  form.fields = {
    ...DEFAULTS.form,
    ...(parsed?.form || {}),
    // 后台把可选项清空会让 C 端没有性别可选，这里兜底回默认三项
    genderOptions:
      Array.isArray(rawGender) && rawGender.length
        ? rawGender.map((item: any) => String(item))
        : [...DEFAULTS.form.genderOptions],
    industries: normalizeList(parsed?.form?.industries, DEFAULTS.form.industries),
    positions: normalizeList(parsed?.form?.positions, DEFAULTS.form.positions)
  }
  form.login = { ...DEFAULTS.login, ...(parsed?.login || {}) }
  form.home = { ...DEFAULTS.home, ...(parsed?.home || {}) }
}

/** 表单 → 整段 JSON（保存与高级模式共用） */
const buildPayload = () => ({
  startup: { ...form.startup },
  onboarding: {
    ...form.onboarding,
    // 候选里已被删掉的选项，不要残留在「默认选中」里
    tagsSelected: form.onboarding.tagsSelected.filter((item) => form.onboarding.tags.includes(item)),
    directionsSelected: form.onboarding.directionsSelected.filter((item) =>
      form.onboarding.directions.includes(item)
    ),
    steps: form.onboarding.steps.map((item) => ({ ...item }))
  },
  form: { ...form.fields, genderOptions: [...form.fields.genderOptions] },
  login: { ...form.login },
  home: { ...form.home }
})

const prettyJson = () => JSON.stringify(buildPayload(), null, 2)

const load = async () => {
  loading.value = true
  try {
    const data = await Api.getAppCopy()
    const raw = typeof data === 'string' ? data : JSON.stringify(data ?? {})
    let parsed: any = {}
    try {
      parsed = raw ? JSON.parse(raw) : {}
    } catch (e) {
      message.warning('后台配置不是合法 JSON，已按内置默认展示，请检查「高级 JSON」')
    }
    applyJsonToForm(parsed)
    jsonText.value = prettyJson()
  } catch (e) {
    message.error('读取文案配置失败，请检查后台服务')
  } finally {
    loading.value = false
  }
}

const validateJson = (text: string) => {
  const parsed = JSON.parse(text)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('必须是 JSON 对象')
  }
  return parsed
}

const save = async () => {
  let content = ''
  try {
    content =
      tab.value === 'json' ? JSON.stringify(validateJson(jsonText.value), null, 2) : prettyJson()
  } catch (e: any) {
    message.error(`JSON 格式错误：${e?.message || '请检查「高级 JSON」'}`)
    return
  }
  saving.value = true
  try {
    await Api.updateAppCopy(content)
    message.success('已保存，App 下次启动生效')
    if (tab.value !== 'json') jsonText.value = content
  } catch (e) {
    message.error('保存失败，请检查后台服务或登录状态')
  } finally {
    saving.value = false
  }
}

const reset = async () => {
  try {
    await message.delConfirm('恢复默认会删除当前文案配置，C 端回到内置文案，确定吗？')
  } catch {
    return
  }
  resetting.value = true
  try {
    await Api.resetAppCopy()
    message.success('已恢复默认文案')
    await load()
  } catch (e) {
    message.error('恢复默认失败')
  } finally {
    resetting.value = false
  }
}

const formatJson = () => {
  try {
    jsonText.value = JSON.stringify(validateJson(jsonText.value), null, 2)
  } catch (e: any) {
    message.error(`JSON 格式错误：${e?.message || ''}`)
  }
}

const applyJson = () => {
  try {
    applyJsonToForm(validateJson(jsonText.value))
    message.success('已应用到表单')
  } catch (e: any) {
    message.error(`JSON 格式错误：${e?.message || ''}`)
  }
}

/** 切到高级模式时同步当前表单；从高级切回时把 JSON 回填表单 */
const onTabChange = (name: string | number) => {
  if (name === 'json') {
    jsonText.value = prettyJson()
    return
  }
  if (!jsonText.value) return
  try {
    applyJsonToForm(validateJson(jsonText.value))
  } catch {
    // JSON 还没改好就切回表单：保留表单现状，不打断编辑
  }
}

onMounted(load)
</script>

<style scoped>
.config-tip {
  margin-left: 10px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.config-preview-img {
  width: 64px;
  height: 40px;
  margin-left: 10px;
  border-radius: 4px;
  border: 1px solid var(--el-border-color);
}

.step-form {
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
}

.step-title {
  margin-bottom: 10px;
  font-weight: 600;
}

.json-box :deep(textarea) {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.6;
}

.preview-label {
  margin-bottom: 8px;
  font-weight: 600;
}

.phone-preview {
  padding: 18px 16px 16px;
  border-radius: 14px;
  background: #0b0b0d;
  color: #f7f3e9;
}

.preview-welcome {
  font-size: 10px;
  letter-spacing: 2px;
  color: #45e0be;
  margin-bottom: 10px;
}

.preview-title {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  white-space: pre-wrap;
  background: linear-gradient(120deg, #8fe6ff, #45e0be 60%, #26b893);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.preview-desc {
  margin-top: 10px;
  font-size: 11px;
  line-height: 1.7;
  color: rgba(247, 243, 233, 0.6);
}

.preview-card {
  position: relative;
  height: 120px;
  margin-top: 12px;
  border: 1px solid rgba(69, 224, 190, 0.35);
  border-radius: 12px;
  background: radial-gradient(circle at 50% 45%, rgba(69, 224, 190, 0.35), transparent 65%);
}

.preview-badge {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  font-size: 9px;
  color: #45e0be;
  background: rgba(6, 9, 11, 0.72);
  border: 1px solid rgba(69, 224, 190, 0.45);
  border-radius: 10px;
}

.preview-primary {
  margin-top: 12px;
  padding: 9px 0;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #07120f;
  border-radius: 20px;
  background: linear-gradient(135deg, #8fe6ff, #45e0be 54%, #26b893);
}

.preview-skip {
  margin-top: 8px;
  padding: 8px 0;
  text-align: center;
  font-size: 11px;
  color: rgba(247, 243, 233, 0.45);
  border: 1px solid rgba(247, 243, 233, 0.16);
  border-radius: 20px;
}

.preview-tip {
  margin-top: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
</style>
